(ns app.front.view.group.admin.public-records.core
  (:require [app.domain.branding.model :as branding-model]
            [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.group.admin.feature-toggle :as feature-toggle]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [app.rich-text :as rich-text]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.i18n :as i18n]
            [core.user.model :as user-model]
            [core.util.schemas :as schemas]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

;; All editable fields on this page store their drafts in a single
;; `::drafts` map keyed by a draft-key (keyword or vector).
;; `::set-draft` compares the new value against the original using
;; an optional `normalize-fn`; when they match the draft is removed,
;; keeping `::save-allowed?` accurate.

(def ^:private localized-draft-keys
  #{:group/complaint-display-name
    :group/public-records-description
    :group/contact-request-description
    :group/complaint-description
    :group/public-map-info-text})

(defn- normalize-rt-strings
  "Normalize a localized strings map by extracting plain text from each
   value so HTML serialization differences don't affect equality."
  [m]
  (into {} (map (fn [[k v]] [k (rich-text/join-rich-text-content v)])) m))

(rf/reg-event-db ::set-draft
  (fn [db [_ draft-key value original normalize-fn]]
    (let [norm (or normalize-fn identity)]
      (if (= (norm value) (norm original))
        (-> db
            (dissoc ::drafts)
            (p/assoc-if ::drafts (not-empty (dissoc (::drafts db) draft-key))))
        (assoc-in db [::drafts draft-key] value)))))

(rf/reg-event-db ::cancel-all
  (fn [db _]
    (-> db
        (dissoc ::drafts)
        (update ::editor-generation (fnil inc 0)))))

(defn- inc-editor-generation [db]
  (update db ::editor-generation (fnil inc 0)))

;; ---- Events ----

(rf/reg-event-fx ::authority-users-fetch-success
  (fn [{:keys [db]} [_ group-id status response]]
    {:db       (assoc-in db [::authority-users group-id] (keys response))
     :dispatch [::entities/merge-entity-db status response]}))

(rf/reg-event-fx ::fetch-authority-users
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/authority-users-for-domain-group
                    :method     :get
                    :data       (util/mmap group-id)
                    :on-success [::authority-users-fetch-success group-id]}}))

(rf/reg-event-fx ::set-contact-request-recipient
  (fn [_ [_ user-id group-id]]
    {:comm/command {:command    :group/set-contact-request-recipient
                    :method     :post
                    :data       (util/mmap user-id group-id)
                    :notify     {:success :success}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::set-contact-request-recipients
  (fn [{:keys [db]} [_ group-id]]
    (let [emails (str-util/blank->nil (get-in db [::drafts :contact-request-recipients]))]
      {:comm/command {:command    :group/set-contact-request-recipients
                      :method     :post
                      :data       (util/mmap {:emails (some-> emails
                                                              (str/split #",")
                                                              (->> (map str/trim))
                                                              set)}
                                             group-id)
                      :notify     {:success :success}
                      :on-success [::entities/apply-changes]}})))

(rf/reg-event-fx ::save-all
  (fn [{:keys [db]} [_ group-id]]
    (let [drafts (::drafts db)]
      {:fx (cond-> []
             (some (fn [[k _]] (localized-draft-keys k))
                   drafts)
             (conj [:dispatch [::save-descriptions group-id]])
             (contains? drafts :contact-request-recipients)
             (conj [:dispatch [::set-contact-request-recipients group-id]]))})))

(rf/reg-event-fx ::save-descriptions
  (fn [{:keys [db]} [_ group-id]]
    (let [descriptions (select-keys (::drafts db) localized-draft-keys)]
      {:comm/command {:command    :group/save-public-info-descriptions
                      :method     :post
                      :data       (merge {:group-id group-id} descriptions)
                      :notify     {:success :success}
                      :on-success [::save-descriptions.success]}})))

(rf/reg-event-fx ::save-descriptions.success
  (fn [{:keys [db]} [_ status response]]
    (let [drafts (not-empty (apply dissoc (::drafts db) localized-draft-keys))]
      {:db (-> (dissoc db ::drafts)
               (p/assoc-if ::drafts drafts)
               inc-editor-generation)
       :fx [[:dispatch [::entities/apply-changes status response]]]})))

(rf/reg-event-fx ::toggle-show-complaints
  (fn [_ [_ group-id enabled]]
    {:comm/command {:command    :group/toggle-public-map-show-complaints
                    :method     :post
                    :data       (util/mmap group-id enabled)
                    :notify     {:success :success}
                    :on-success [::entities/apply-changes]}}))

;; ---- Subscriptions ----

(rf/reg-sub ::drafts
  :-> ::drafts)

(rf/reg-sub ::draft
  :<- [::drafts]
  (fn [drafts [_ draft-key]]
    (get drafts draft-key)))

(defn- valid-email-list?
  "Returns true when `value` is blank or every comma-separated entry is a valid email."
  [value]
  (let [trimmed (str-util/blank->nil value)]
    (or (nil? trimmed)
        (->> (str/split trimmed #",")
             (every? #(nil? (s/check schemas/Email (str/trim %))))))))

(rf/reg-sub ::save-allowed?
  :<- [::drafts]
  (fn [drafts _]
    (and (seq drafts)
         (valid-email-list? (get drafts :contact-request-recipients)))))

(rf/reg-sub ::has-complaint-permit-types?
  :<- [::gas/group]
  (fn [group _]
    (boolean (some ptc-model/permit-types-with-complaints
                   (group-model/get-permit-types group)))))

(rf/reg-event-fx ::fetch-complaint-configs
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/complaint-group-configs
                    :method     :get
                    :data       (util/mmap group-id)
                    :on-success [::fetch-complaint-configs.success]}}))

(rf/reg-event-fx ::fetch-complaint-configs.success
  (fn [{:keys [db]} [_ _status response]]
    {:db (assoc db ::complaint-group-configs response)}))

(rf/reg-sub ::complaint-group-configs
  :-> ::complaint-group-configs)

(rf/reg-sub ::editor-generation
  :-> ::editor-generation)

(rf/reg-sub ::authority-users-by-group
  :-> ::authority-users)

(rf/reg-sub ::authority-users
  :<- [::authority-users-by-group]
  :<- [::gas/group-id]
  (fn [[users-map group-id] _]
    (get users-map group-id)))

(rf/reg-sub ::auth-users-sorted
  :<- [::authority-users]
  :<- [::entities/entity-db]
  :<- [::entities/components]
  :<- [::entities/schema]
  (fn [[auth-users entity-db components schema] _]
    (->> (entities/find-entities {:entity-db entity-db :components components :schema schema}
                                 (filter #(some (set auth-users) #{(:db/id %)})))
         (sort user-model/user-sort-lastname))))

;; ---- Helper functions ----

(defn- user->select-item [user]
  {:id       (:db/id user)
   :fullname (or (str-util/blank->nil (user-model/full-name user))
                 (:user/email user))})

(defn public-records-link
  [domain-group]
  [:div.flex-row.mt-5
   [:div.mt-2
    (let [branding (branding-model/or-default (:group/uri-path-id domain-group))
          href     (str (.-protocol js/window.location) "//"
                        (.-host js/window.location) "/"
                        branding
                        "/public-records")]
      (cc/link {:href   href
                :target "_blank"}
               href))]])

(defn- complaint-display-name-editor [tr group]
  (let [attr       :group/complaint-display-name
        original   (or (i18n-model/strings-map (attr group)) {})
        strings    (or (<sub [::draft attr]) original)
        language   (<sub [::ui-i18n/editor-language ::gas/allowed-locales])
        tempura-tr (<sub [::i18n/tempura-tr])]
    [:div.mt-5
     [ui-i18n/localized-string-edit
      {:id                  "complaint-display-name-editor"
       :label-text          (tr [:group/complaint-display-name])
       :placeholder         (tempura-tr [language] [:public-records/file-complaint])
       :strings             strings
       :auto-focus          false
       :allowed-locales-sub ::gas/allowed-locales
       :on-change           #(rf/dispatch-sync [::set-draft attr % original])}]]))

;; ---- Tile description editor ----

(def ^:private default-description-keys
  {:group/public-records-description  :landing-page/default-public-records-description
   :group/contact-request-description :landing-page/default-contact-request-description
   :group/complaint-description       :landing-page/default-complaint-description})

(defn- defaults-strings-map
  "Build a `{lang html-string}` map of default tile descriptions for all
   `allowed-locales`, with the municipality name interpolated."
  [tempura-tr allowed-locales attr municipality-name]
  (when-let [k (get default-description-keys attr)]
    (into {}
          (map (fn [lang]
                 [lang (tempura-tr [lang] [k] [municipality-name])]))
          allowed-locales)))

(defn- tile-description-editor
  "Localized text editor for a public info tile description.
   Writes to draft state in app-db; saved by the page-level save button.
   When the group has no description configured yet, pre-populates the editor
   with the localized default text so admins can see and edit it."
  [{:keys [tr group attr editor-id]}]
  (let [draft           (<sub [::draft attr])
        generation      (<sub [::editor-generation])
        configured      (i18n-model/strings-map (get group attr))
        tempura-tr      (<sub [::i18n/tempura-tr])
        allowed-locales (<sub [::gas/allowed-locales])
        defaults        (when (empty? configured)
                          (defaults-strings-map tempura-tr allowed-locales attr (:group/name group)))
        original        (or (not-empty configured)
                            defaults
                            {})
        strings         (or draft original)]
    [:div.mt-5
     ^{:key generation}
     [ui-i18n/localized-string-edit
      {:id                  editor-id
       :multiline?          true
       :label-text          (tr [:group/tile-description])
       :strings             strings
       :allowed-locales-sub ::gas/allowed-locales
       :on-change           #(>evt [::set-draft attr % original normalize-rt-strings])}]]))

;; ---- Contact request recipients section ----

(defn- contact-request-recipients-section [tr group auth-users group-id]
  (let [original (->> group :group/contact-request-recipients (str/join ","))]
    [:<>
     [ui/help (tr [:public-map-view/contact-request-recipient-help-text])]
     (when (and (nil? (:group/contact-request-recipient group))
                (empty? (:group/contact-request-recipients group)))
       (cc/inline-notification {:title             (tr [:public-map-view/contact-request-no-recipients-title])
                                :subtitle          (tr [:public-map-view/contact-request-no-recipients-subtitle])
                                :kind              :warning
                                :hide-close-button true}))
     [:div.flex-row.mt-5
      [ui-select/select {:values       (->> auth-users
                                            (map user->select-item)
                                            (concat [{:id       nil
                                                      :fullname (tr [:public-map-view/no-recipient])}]))
                         :value        (user->select-item (:group/contact-request-recipient group))
                         :allow-empty? false
                         :val->str     :fullname
                         :label-text   (tr [:public-map-view/contact-request-recipient])
                         :id           "contact-request-recipient"
                         :on-change    #(>evt [::set-contact-request-recipient (:id %) group-id])}]]
     [:div.flex-row.mt-5
      [ui/text-area
       {:id         "contact-request-recipients"
        :value      (or (<sub [::draft :contact-request-recipients])
                        original)
        :label-text (tr [:public-map-view/additional-contact-request-recipients])
        :on-change  #(rf/dispatch-sync [::set-draft :contact-request-recipients (futil/tval %) original str-util/blank->nil])}]]]))

;; ---- Public map info section ----

(defn- public-map-info-section [tr group title-tr-key]
  (let [draft           (<sub [::draft :group/public-map-info-text])
        generation      (<sub [::editor-generation])
        configured      (i18n-model/strings-map (:group/public-map-info-text group))
        old-unlocalized (when (empty? configured)
                          (some-> (:group/info-text-for-public-map group)
                                  (->> (hash-map :en))))
        original        (or (not-empty configured) old-unlocalized {})
        strings         (or draft original)]
    [:<>
     [:div.flex-row.mt-10
      [:h5 (tr [title-tr-key])]]
     [:div.mt-5
      ^{:key generation}
      [ui-i18n/localized-rich-text-editor
       {:id                  "public-map-info-editor"
        :label-text          (tr [title-tr-key])
        :strings             strings
        :allowed-locales-sub ::gas/allowed-locales
        :on-change           #(>evt [::set-draft :group/public-map-info-text % original normalize-rt-strings])}]]]))

;; ---- Settings section helper ----

(defn- settings-section [tr title lang-selector? & content]
  (into [:section
         [:div.flex.justify-between.items-center.mb-3
          [:h4 (tr [title])]
          (when (and lang-selector?
                     (-> (<sub [::gas/allowed-locales])
                         rest
                         seq))
            [:div.flex.items-center.gap-2
             [:span (str (tr [:language/for-language]) ":")]
             [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales
                                              :dropdown-class      ""
                                              :hide-label          true}]])]]
        content))

;; ---- Public Information view ----

(defn public-information-view [tr group group-id auth-users]
  (let [map-enabled?             (contains? (:group/features group) :group.feature/domain-public-map)
        contact-request-enabled? (contains? (:group/features group) :group.feature/contact-request)
        has-complaint-types?     (<sub [::has-complaint-permit-types?])
        show-complaints?         (:group/public-map-show-complaints group)]
    [:<>
     [:p.mt-4 (tr [:group/public-information-page-description])]

     ;; --- Public records section ---
     [settings-section tr :group/public-records-section true
      [:div.flex-row
       (cc/toggle {:id         "public-map-toggle"
                   :class-name "mt-2"
                   :label-text (tr [:group/enable-public-records])
                   :toggled    (<sub [::gas/has-feature? :group.feature/domain-public-map])
                   :on-toggle  (fn [checked]
                                 (>evt [::feature-toggle/toggle-group-feature
                                        group-id
                                        :group.feature/domain-public-map
                                        checked]))})
       (when map-enabled?
         [public-records-link group])]
      (when map-enabled?
        [:<>
         [public-map-info-section tr group :group/public-records-info]
         [tile-description-editor {:tr        tr
                                   :group     group
                                   :attr      :group/public-records-description
                                   :editor-id "public-records-description-editor"}]])]

     ;; --- Contact request section ---
     [settings-section tr :group/contact-request-section true
      [:div.flex-row
       (cc/toggle {:id         "contact-request-toggle"
                   :class-name "mt-2"
                   :label-text (tr [:group/enable-contact-request])
                   :toggled    (<sub [::gas/has-feature? :group.feature/contact-request])
                   :on-toggle  (fn [checked]
                                 (>evt [::feature-toggle/toggle-group-feature
                                        group-id
                                        :group.feature/contact-request
                                        checked]))})]
      (when contact-request-enabled?
        [:<>
         [contact-request-recipients-section tr group auth-users group-id]
         [tile-description-editor {:tr        tr
                                   :group     group
                                   :attr      :group/contact-request-description
                                   :editor-id "contact-request-description-editor"}]])]

     ;; --- File a complaint section ---
     (when has-complaint-types?
       [settings-section tr :group/file-complaint-section true
        (cc/toggle {:id         "file-complaint-toggle"
                    :class-name "mt-2"
                    :label-text (tr [:group/enable-file-complaint])
                    :toggled    (boolean show-complaints?)
                    :on-toggle  (fn [checked]
                                  (>evt [::toggle-show-complaints group-id checked]))})
        (when show-complaints?
          [:<>
           [complaint-display-name-editor tr group]
           [tile-description-editor {:tr        tr
                                     :group     group
                                     :attr      :group/complaint-description
                                     :editor-id "complaint-description-editor"}]])])

     ;; --- Page-level save/cancel ---
     [:div.flex.justify-end.mt-8
      [ui-action-buttons/save-cancel-buttons
       {:on-cancel      #(>evt [::cancel-all])
        :on-save        #(>evt [::save-all group-id])
        :save-disabled? (not (<sub [::save-allowed?]))
        :size           :small
        :tr             tr}]]]))
